/* eslint-disable max-len */
import Shepherd from 'shepherd.js';
import { UserAgent } from '../../platform/UserAgent';
import { AccountModel } from '../account/AccountModel';
import { AppNavigator } from '../AppNavigator';
import { Resolver } from '../Container';
import { PassportModel } from '../PassportModel';
import { ProfileModel } from '../ProfileModel';
import { remInPx } from './../../ui';

const named = (name: string) => `[data-tour="${name}"]`;

const canAttachTo = (selector: string) => !!document.querySelector(selector);

const attachTo = (selector: string, on: any) => ({
    attachTo: { element: selector, on }
});

const backNextButtons = () => ({
    buttons: [
        {
            action() {
                return this.back();
            },
            classes: 'shepherd-button-secondary',
            text: 'Back'
        },
        {
            action() {
                return this.next();
            },
            text: 'Next'
        }
    ]
});

const offset = (x = 0, y = 0): any => ({
    popperOptions: {
        modifiers: [{
            name: 'offset',
            options: { offset: [remInPx(x), remInPx(y)] }
        }]
    },
});

const padding = (rem: number) => ({
    modalOverlayOpeningPadding: remInPx(rem),
});

export const DashboardTour = (r: Resolver) => {

    const navigator = r.call(AppNavigator);
    const passport = r.call(PassportModel);
    const { trial, plan } = r.call(AccountModel);

    const goto = (path: string) => ({
        when: {
            'before-show': () => navigator.go(path)
        }
    });

    const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
            cancelIcon: {
                enabled: true
            },
            classes: 'deqo',
            scrollTo: { behavior: 'smooth', block: 'center' }
        },
    });

    tour.addStep({
        title: 'Navigation',
        text: 'First things first, this is the primary navigation for your team space.  It has links to all the areas of the application.',
        ...attachTo(named('nav-primary'), 'right'),
        ...goto('/boards'),
        buttons: [
            {
                action() {
                    return this.next();
                },
                text: 'Next'
            }
        ],
    });

    if (passport.verify('ac_team:write')) {
        tour.addStep({
            title: 'The Bottom Bit',
            text: 'Down here is your team name and some buttons to invite team members and access the admin area.',
            ...attachTo(named('nav-admin'), 'top-start'),
            ...backNextButtons(),
            ...goto('/boards')
        });
    }
    else {
        tour.addStep({
            title: 'The Bottom Bit',
            text: 'Down here is your team name and the button to invite team members.',
            ...attachTo(named('nav-admin'), 'top-start'),
            ...backNextButtons(),
            ...goto('/boards')
        });
    }

    if (canAttachTo(named('ab-template'))) {
        tour.addStep({
            title: 'Using Templates',
            text: 'To create a new board from one of our many templates, use this button.  We recommend this if you are new to the tool!',
            ...attachTo(named('ab-template'), 'bottom'),
            ...padding(1),
            ...offset(0, 1),
            ...backNextButtons(),
            ...goto('/boards')
        });
    }

    if (canAttachTo(named('ab-blank'))) {
        tour.addStep({
            title: 'Blank Boards',
            text: 'Or to create a completely blank board and start from scratch, use this one.',
            ...attachTo(named('ab-blank'), 'bottom'),
            ...padding(1),
            ...offset(0, 1),
            ...backNextButtons(),
            ...goto('/boards')
        });
    }

    tour.addStep({
        title: 'News & Help',
        text: 'Finally, here you can find our release notes / announcements feed, preferences menu and help menu.  There are lots of resources in here to help with using Ludi, including taking this tour again, if that\'s your bag.',
        ...attachTo(named('header-help'), 'left'),
        ...offset(0, 1),
        buttons: [
            {
                action() {
                    return this.next();
                },
                text: 'Finish'
            }
        ],
    });

    return tour;

};

export const ChangesTour = (r: Resolver) => {

    const navigator = r.call(AppNavigator);

    const goto = (path: string) => ({
        when: {
            'before-show': () => navigator.go(path)
        }
    });

    const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
            cancelIcon: {
                enabled: true
            },
            classes: 'deqo',
            scrollTo: { behavior: 'smooth', block: 'center' }
        },
    });

    tour.addStep({
        title: 'All Boards',
        text: 'Recent Boards has been replaced with All Boards.  This is a new screen that shows you all the boards you have joined or have access to, across all workspaces.',
        ...attachTo(named('nav-boards'), 'right'),
        ...backNextButtons(),
        ...goto('/boards')
    });

    tour.addStep({
        title: 'The Filters',
        text: 'You can swap between viewing boards you have joined, or boards you have access to here.',
        ...attachTo(named('ab-filters'), 'bottom'),
        ...padding(1),
        ...offset(0, 1),
        ...backNextButtons(),
        ...goto('/boards')
    });

    tour.addStep({
        title: 'The Search',
        text: 'Here you can search the board list.  Again, this works across all workspaces, so if you can\'t remember where a board is you can search for it here.',
        ...attachTo(named('ab-search'), 'bottom'),
        ...padding(1),
        ...offset(0, 1),
        ...backNextButtons(),
        ...goto('/boards')
    });


    tour.addStep({
        title: 'The Buttons',
        text: 'You can create boards from the All Boards view.  Since you are not in a workspace here, these boards will be created without an assigned workspace.  If you want to assign one later, you can do that using the Move function.',
        ...attachTo(named('ab-blank'), 'bottom'),
        ...padding(1),
        ...offset(0, 1),
        ...backNextButtons(),
        ...goto('/boards')
    });

    tour.addStep({
        title: 'The Bottom Bit',
        text: 'Finally, we\'ve added an Invite Members button to the bottom bit of the navigation to make it easier to get people into the space.  Anyone can use this button, but non-admins are subject to the access control rules configured in the team settings.',
        ...attachTo(named('nav-admin'), 'right'),
        ...backNextButtons(),
        ...goto('/boards')
    });

    return tour;

};

export const EditorTour = (r: Resolver) => {

    const me = r.call(ProfileModel);

    const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
            cancelIcon: {
                enabled: true
            },
            classes: 'deqo',
            scrollTo: { behavior: 'smooth', block: 'center' }
        },
    });

    if (canAttachTo('#wc-meetings')) {
        tour.addStep({
            title: 'The Meeting Button',
            text: `
                This is the meeting button.  It lets you start a "meeting" on this board.
                Meetings are a central concept for collaborating with others in Ludi.
                They provide a structure for one or more facilitators to frame and guide an interactive meeting.
                You can find our more about meetings <a target="_blank" href="/tutorials">in the tutorials</a>.
            `,
            ...attachTo('#wc-meetings', 'right'),
            ...offset(0, 1.5),
            ...padding(.5),
            ...backNextButtons(),
        });
    }

    tour.addStep({
        title: 'The Toolbox',
        text: `
            Here you you will find all the tools available for making changes to the board.
            If there is a meeting running, the meeting facilitator can change which tools are available.
        `,
        ...attachTo('#wc-toolbox', 'right'),
        ...offset(0, 1.5),
        ...padding(.5),
        ...backNextButtons(),
    });

    tour.addStep({
        title: 'Zoom Controls',
        text: `
            These buttons allow you to change the zoom level of your view.
            You can also change the view with the Mouse Wheel or Trackpad (which is what we recommend really).
        `,
        ...attachTo('#wc-zoom', 'top-start'),
        ...offset(0, 1.5),
        ...padding(.5),
        ...backNextButtons(),
    });

    tour.addStep({
        title: 'Undo & Redo',
        text: `
            Here you can Undo or Redo your last change to the board.  
            As usual, you can also press 
            <code>${UserAgent.os.name === 'Mac OS' ? 'CMD+Z' : 'CTRL+Z'} </code> or
            <code>${UserAgent.os.name === 'Mac OS' ? 'CMD+SHIFT+Z' : 'CTRL+Y'}</code> to do this via the keyboard.
        `,
        ...attachTo('#wc-history', 'top-start'),
        ...offset(0, 1.5),
        ...padding(.5),
        ...backNextButtons(),
    });

    tour.addStep({
        title: 'Time Machine',
        text: `
            Every change made to the board is saved.  
            The Time Machine button lets you step back through the board history.  
            This is useful for restoring things that were accidentally deleted!
        `,
        ...attachTo('#wc-replay', 'top-start'),
        ...offset(0, 1.5),
        ...padding(.5),
        ...backNextButtons(),
    });

    // tour.addStep({
    //     title: 'Gestures',
    //     text: `
    //         Over here is the Gestures menu.
    //         It contains a bunch of fun and useful tools that you can use to interact with other people on the board.
    //         Make sure you try out the confetti tool!
    //     `,
    //     ...attachTo('#wc-gestures', 'top-end'),
    //     ...offset(0, 1.5),
    //     ...padding(.5),
    //     ...backNextButtons(),
    // });

    tour.addStep({
        title: 'People',
        text: `
            Up here is the list of the people who are on the board with you.  
            You always appear at the top.
            You can click on yourself to perform some actions specific to you, and click on others to Follow them or see their board objects.
        `,
        ...attachTo('#wc-participants', 'right-start'),
        ...offset(0, 1.5),
        ...padding(.5),
        ...backNextButtons(),
    });

    if (canAttachTo(named('share'))) {
        tour.addStep({
            title: 'Sharing & Guests',
            text: `
            The Share button lets you control public/private access to the board, as well as manage guests and get the sharable board link to send to others.
            Please note access control and guest users are <a target="_blank" href="https://ludi.co/pricing">premium features</a>.
        `,
            ...attachTo(named('share'), 'bottom-end'),
            ...offset(0, 1.5),
            ...padding(.5),
            ...backNextButtons(),
        });
    }

    if (canAttachTo(named('usage'))) {
        tour.addStep({
            title: 'Free Plan Usage',
            text: `
                As you are not yet a Ludi subscriber, your team is subject to our free plan usage limits.  
                The status of this you can see here.  
                It will update every few minutes.
            `,
            ...attachTo(named('usage'), 'bottom-end'),
            ...offset(0, 1.5),
            ...padding(.5),
            ...backNextButtons(),
        });
    }

    if (me.type === 1) {
        tour.addStep({
            title: 'Board Menu',
            text: `
                    In here you can find options to rename the board, manage who has access and export board data.
                `,
            ...attachTo(named('board-menu'), 'top-start'),
            ...offset(0, 1.5),
            ...padding(.5),
            ...backNextButtons(),
        });

        tour.addStep({
            title: 'Navigation & Home',
            text: `
                    Finally, up here is the navigation button to show your space navigation.  
                    You can also click the Ludi logo to leave the board and go back to the last screen you were on, which is usually the page you accessed the board from.
                `,
            ...attachTo('#canton', 'top-start'),
            ...offset(0, 1.5),
            ...padding(.5),
            ...backNextButtons(),
        });
    }

    tour.addStep({
        title: 'The End',
        text: `
            Alright, that's pretty much everything for now.  
            As you explore the app you will be presented with help tips that explain things further.  
            ${me.type === 1 ? 'If you would like to learn more right away you can visit our <a target="_blank" href="/tutorials">Tutorials Page</a> for more in-depth guides on Ludi features.' : ''}
            <br />
            <br />
            Good luck!
        `,
        buttons: [
            {
                action() {
                    return this.next();
                },
                text: 'Finish'
            }
        ],
    });

    return tour;

};
